import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, IndianRupee, CheckCircle, Loader2, CreditCard, Phone, Building2, History, LogOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { useRazorpay } from 'react-razorpay';
import { formatTime, isSlotPassed } from '../utils/timeSlots';

// Helper functions
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const SLOT_TYPES = {
  FULL: 'full',
  HALF: 'half',
  QUARTER: 'quarter'
};

// Customer Auth Context
const CUSTOMER_TOKEN_KEY = 'customer_token';
const CUSTOMER_DATA_KEY = 'customer_data';

const PublicBooking = () => {
  const { Razorpay } = useRazorpay();
  const navigate = useNavigate();
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [authStep, setAuthStep] = useState('phone'); // 'phone', 'otp', 'profile'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  // Facility state
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  
  // Booking state
  const [view, setView] = useState('booking'); // 'booking', 'history'
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotType, setSelectedSlotType] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [customerDetails, setCustomerDetails] = useState({ name: '', email: '' });
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [bookingHistory, setBookingHistory] = useState([]);
  
  // API data
  const [timeSlots, setTimeSlots] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Reset booking state when switching facilities
  const resetBookingState = useCallback(() => {
    setStep(1);
    setSelectedDate('');
    setSelectedSlotType('');
    setSelectedSection('');
    setSelectedTimeSlots([]);
    setAvailability([]);
    setBookingConfirmed(false);
    setConfirmedBooking(null);
    setPendingBooking(null);
  }, []);

  // Handle facility selection with state reset
  const handleFacilitySelect = useCallback((facility) => {
    resetBookingState();
    setSelectedFacility(facility);
  }, [resetBookingState]);

  // ========== API FUNCTIONS (declared before useEffect hooks that use them) ==========
  
  const loadFacilities = useCallback(async () => {
    try {
      const facilitiesData = await api.getPublicFacilities();
      setFacilities(facilitiesData);
      if (facilitiesData.length === 1) {
        setSelectedFacility(facilitiesData[0]);
      }
    } catch (error) {
      console.error('Error loading facilities:', error);
      setFacilities([]);
    }
    setLoading(false);
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const data = await api.getPublicSettings();
      setSettings(data);
      // Don't overwrite time_slots here - facility-specific slots are generated in useEffect
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }, []);

  const loadAvailability = useCallback(async () => {
    if (!selectedDate || !selectedFacility) return;
    setLoadingSlots(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/slots/public-availability?date=${selectedDate}&facility_id=${selectedFacility.id}`
      );
      const data = await response.json();
      setAvailability(data);
    } catch (error) {
      console.error('Error loading availability:', error);
    }
    setLoadingSlots(false);
  }, [selectedDate, selectedFacility]);

  const loadBookingHistory = async () => {
    try {
      const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/auth/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setBookingHistory(data);
    } catch (error) {
      console.error('Error loading booking history:', error);
    }
  };

  // ========== USE EFFECT HOOKS ==========

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    const savedCustomer = localStorage.getItem(CUSTOMER_DATA_KEY);
    
    if (token && savedCustomer) {
      try {
        const customerData = JSON.parse(savedCustomer);
        setCustomer(customerData);
        setIsAuthenticated(true);
        setCustomerDetails({ name: customerData.name || '', email: customerData.email || '' });
      } catch (e) {
        localStorage.removeItem(CUSTOMER_TOKEN_KEY);
        localStorage.removeItem(CUSTOMER_DATA_KEY);
      }
    }
    
    // Load facilities
    loadFacilities();
  }, [loadFacilities]);

  // Load settings and time slots when facility is selected
  useEffect(() => {
    if (selectedFacility) {
      loadSettings();
      // Generate time slots from facility operating hours
      if (selectedFacility.operating_hours) {
        const startHour = parseInt(selectedFacility.operating_hours.start.split(':')[0]);
        const endHour = parseInt(selectedFacility.operating_hours.end.split(':')[0]);
        const slots = [];
        
        // Check if overnight facility (closing time is before opening time)
        const isOvernightFacility = endHour < startHour;
        
        if (isOvernightFacility) {
          // First, add early morning slots (12:00 AM to closing time)
          for (let hour = 0; hour < endHour; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
              slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
            }
          }
          // Then add normal hours (opening time to 11:30 PM)
          for (let hour = startHour; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
              slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
            }
          }
        } else {
          // Normal hours (same day)
          for (let hour = startHour; hour <= endHour; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
              if (hour === endHour && minute > 30) break;
              slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
            }
          }
        }
        setTimeSlots(slots);
      }
    }
  }, [selectedFacility, loadSettings]);

  // Load availability when date and slot type change
  useEffect(() => {
    if (selectedDate && selectedSlotType && selectedFacility) {
      loadAvailability();
    }
  }, [selectedDate, selectedSlotType, selectedFacility, loadAvailability]);

  // Auth functions
  const handleSendOtp = async () => {
    if (phone.length !== 10) {
      toast({ title: "Invalid Phone", description: "Please enter a valid 10-digit phone number", variant: "destructive" });
      return;
    }
    
    setSendingOtp(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "OTP Sent", description: data.message });
        setAuthStep('otp');
      } else {
        toast({ title: "Error", description: data.detail || "Failed to send OTP", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send OTP", variant: "destructive" });
    }
    setSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 4) {
      toast({ title: "Invalid OTP", description: "Please enter 4-digit OTP", variant: "destructive" });
      return;
    }
    
    setVerifyingOtp(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp })
      });
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, data.token);
        localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(data.customer));
        setCustomer(data.customer);
        setCustomerDetails({ name: data.customer.name || '', email: data.customer.email || '' });
        setIsAuthenticated(true);
        
        if (data.customer.is_new) {
          setAuthStep('profile');
        } else {
          toast({ title: "Welcome back!", description: `Logged in as ${data.customer.name || phone}` });
        }
      } else {
        toast({ title: "Invalid OTP", description: data.detail || "Please try again", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to verify OTP", variant: "destructive" });
    }
    setVerifyingOtp(false);
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/auth/profile`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: customerDetails.name, email: customerDetails.email })
      });
      const data = await response.json();
      
      setCustomer(data);
      localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(data));
      setAuthStep('phone');
      toast({ title: "Profile Saved", description: "Your details have been saved" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to save profile", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_DATA_KEY);
    setIsAuthenticated(false);
    setCustomer(null);
    setAuthStep('phone');
    setPhone('');
    setOtp('');
    setSelectedFacility(null);
    resetBooking();
  };

  const getSlotStatus = (time, section) => {
    if (!availability || !availability.slots) return 'available';
    
    const slotData = availability.slots[time];
    if (!slotData) return 'available';
    
    // For football 'full' booking
    if (selectedSlotType === 'full') {
      return slotData.full_available ? 'available' : 'unavailable';
    }
    
    if (!slotData.sections) return 'available';
    const sectionData = slotData.sections[section];
    return sectionData?.available ? 'available' : 'unavailable';
  };

  const handleTimeSlotClick = (time) => {
    // Prevent selecting passed time slots
    if (isSlotPassed(selectedDate, time)) return;
    
    const isFootball = selectedFacility?.sports?.includes('football');
    
    // For PicknPadel, check if ANY court of the sport is available
    let status = 'unavailable';
    if (isFootball) {
      const section = selectedSlotType === 'full' ? 'full' : selectedSection;
      status = getSlotStatus(time, section);
    } else {
      // Check if any court of this sport type is available
      const courtsOfSport = selectedFacility?.courts?.filter(c => c.sport === selectedSlotType) || [];
      for (const court of courtsOfSport) {
        if (getSlotStatus(time, court.id) === 'available') {
          status = 'available';
          break;
        }
      }
    }
    
    if (status !== 'available') return;
    
    if (selectedTimeSlots.includes(time)) {
      setSelectedTimeSlots(selectedTimeSlots.filter(t => t !== time));
    } else {
      // Allow multiple consecutive slots
      const sortedSlots = [...selectedTimeSlots, time].sort((a, b) => {
        return timeSlots.indexOf(a) - timeSlots.indexOf(b);
      });
      setSelectedTimeSlots(sortedSlots);
    }
  };

  const getSlotPrice = (slotType, time) => {
    if (!selectedFacility?.pricing) return 0;
    
    // Check if it's weekend
    const selectedDateObj = selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date();
    const isWeekend = selectedDateObj.getDay() === 0 || selectedDateObj.getDay() === 6;
    const dayType = isWeekend ? 'weekend' : 'weekday';
    
    // Check if time is in peak hours
    const hour = parseInt(time.split(':')[0]);
    const minute = parseInt(time.split(':')[1] || '0');
    const timeMinutes = hour * 60 + minute;
    
    let isPeak = false;
    const peakHours = selectedFacility.peak_hours?.[dayType] || [];
    for (const range of peakHours) {
      const startMins = parseInt(range.start.split(':')[0]) * 60 + parseInt(range.start.split(':')[1] || '0');
      const endMins = parseInt(range.end.split(':')[0]) * 60 + parseInt(range.end.split(':')[1] || '0');
      if (timeMinutes >= startMins && timeMinutes < endMins) {
        isPeak = true;
        break;
      }
    }
    
    const pricing = selectedFacility.pricing[slotType];
    if (!pricing) return 0;
    
    const dayPricing = pricing[dayType];
    if (!dayPricing) return 0;
    
    // Return hourly rate
    return isPeak ? (dayPricing.peak || 0) : (dayPricing.off_peak || 0);
  };

  // Calculate price for a 30-minute slot (half of hourly rate)
  const getSlotPriceFor30Min = (slotType, time) => {
    return getSlotPrice(slotType, time) / 2;
  };

  const calculateTotalPrice = () => {
    // Each slot is 30 minutes, so divide hourly rate by 2
    return selectedTimeSlots.reduce((sum, time) => {
      return sum + getSlotPriceFor30Min(selectedSlotType, time);
    }, 0);
  };

  // Handle Razorpay payment
  const handlePayment = useCallback(async (booking, amount) => {
    if (!Razorpay) {
      toast({ title: "Payment Gateway Loading", description: "Please wait...", variant: "destructive" });
      return;
    }
    
    setProcessingPayment(true);
    try {
      const orderData = await api.createPublicPaymentOrder(booking.id, amount);
      
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: selectedFacility?.name || "Sports Booking",
        description: `Booking for ${booking.date}`,
        order_id: orderData.order_id,
        prefill: {
          name: customerDetails.name,
          email: customerDetails.email || "",
          contact: customer?.phone || ""
        },
        theme: { color: "#2563eb" },
        handler: async function (response) {
          try {
            const verifyData = await api.verifyPublicPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: booking.id
            });
            
            if (verifyData.success) {
              setConfirmedBooking({ ...booking, payment_status: 'paid', status: 'confirmed' });
              setBookingConfirmed(true);
              setPendingBooking(null);
              toast({ title: "Payment Successful!", description: "Your booking has been confirmed." });
            }
          } catch (err) {
            toast({ title: "Payment Verification Failed", description: err.message, variant: "destructive" });
          }
          setProcessingPayment(false);
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
            toast({ title: "Payment Cancelled", description: "Please complete payment to confirm booking.", variant: "destructive" });
          }
        }
      };
      
      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (err) {
      setProcessingPayment(false);
      toast({ title: "Payment Error", description: err.message, variant: "destructive" });
    }
  }, [Razorpay, selectedFacility, customerDetails, customer]);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    const startTime = selectedTimeSlots[0];
    const endTimeIndex = timeSlots.indexOf(selectedTimeSlots[selectedTimeSlots.length - 1]) + 1;
    const endTime = timeSlots[endTimeIndex] || '23:30';
    const totalPrice = calculateTotalPrice();

    const bookingData = {
      facility_id: selectedFacility?.id,
      date: selectedDate,
      start_time: startTime,
      end_time: endTime,
      slot_type: selectedSlotType,
      section: selectedSection || null,
      court_id: selectedSection,
      sport: selectedFacility?.sports?.[0] || 'football',
      customer_name: customerDetails.name,
      customer_email: customerDetails.email,
      customer_phone: customer?.phone || '',
      status: 'pending',
      amount: totalPrice,
      amount_paid: 0,
      payment_status: 'pending',
      source: 'online',  // Customer bookings via public page are 'online'
      booking_type: 'b2c'
    };

    setSubmitting(true);
    try {
      const newBooking = await api.createPublicBooking(bookingData);
      setPendingBooking(newBooking);
      setSubmitting(false);
      await handlePayment(newBooking, totalPrice);
    } catch (err) {
      toast({ title: "Booking Failed", description: err.message, variant: "destructive" });
      setSubmitting(false);
    }
  };

  const resetBooking = () => {
    setStep(1);
    setSelectedDate('');
    setSelectedSlotType('');
    setSelectedSection('');
    setSelectedTimeSlots([]);
    setBookingConfirmed(false);
    setConfirmedBooking(null);
    setPendingBooking(null);
  };

  // Generate date options (next 14 days)
  const getDateOptions = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const getSections = () => {
    if (selectedSlotType === 'half') {
      return [
        { id: 'north', name: 'North Half (9v9)' },
        { id: 'south', name: 'South Half (9v9)' }
      ];
    }
    if (selectedSlotType === 'quarter') {
      return [
        { id: 'north-east', name: 'North East Quarter (7v7)' },
        { id: 'north-west', name: 'North West Quarter (7v7)' },
        { id: 'south-east', name: 'South East Quarter (7v7)' },
        { id: 'south-west', name: 'South West Quarter (7v7)' }
      ];
    }
    return [];
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white py-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_fieldmaster-24/artifacts/iuxgdyho_Spaces-Logo.png" 
              alt="Spaces by TRP Sports" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">Spaces by TRP Sports</h1>
            <p className="text-gray-600">Sign in to book your slot</p>
          </div>

          <Card className="border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-700">{authStep === 'otp' ? 'Enter OTP' : 'Sign In'}</CardTitle>
              <CardDescription>
                {authStep === 'otp' 
                  ? `We sent a code to ${phone.slice(0, 3)}****${phone.slice(-3)}`
                  : 'Enter your mobile number to continue'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {authStep === 'phone' && (
                <>
                  <div>
                    <Label htmlFor="phone">Mobile Number</Label>
                    <div className="flex mt-1">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        +91
                      </span>
                      <Input
                        id="phone"
                        type="tel"
                        maxLength={10}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                        placeholder="9876543210"
                        className="rounded-l-none"
                      />
                    </div>
                  </div>
                  <Button onClick={handleSendOtp} className="w-full bg-blue-600 hover:bg-blue-700" disabled={sendingOtp || phone.length !== 10}>
                    {sendingOtp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}
                    {sendingOtp ? 'Sending...' : 'Send OTP'}
                  </Button>
                </>
              )}

              {authStep === 'otp' && (
                <>
                  <div>
                    <Label htmlFor="otp">Enter OTP</Label>
                    <Input
                      id="otp"
                      type="text"
                      maxLength={4}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="1234"
                      className="text-center text-2xl tracking-widest"
                    />
                    <p className="text-xs text-gray-500 mt-1">For testing, use OTP: 1234</p>
                  </div>
                  <Button onClick={handleVerifyOtp} className="w-full bg-blue-600 hover:bg-blue-700" disabled={verifyingOtp || otp.length !== 4}>
                    {verifyingOtp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setAuthStep('phone'); setOtp(''); }} className="w-full">
                    Change Number
                  </Button>
                </>
              )}

              {authStep === 'profile' && (
                <>
                  <p className="text-sm text-gray-600">Complete your profile to continue</p>
                  <div>
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      id="name"
                      value={customerDetails.name}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email (Optional)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerDetails.email}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                      placeholder="your@email.com"
                    />
                  </div>
                  <Button onClick={handleSaveProfile} className="w-full bg-blue-600 hover:bg-blue-700" disabled={!customerDetails.name}>
                    Save & Continue
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Facility Selection (after login)
  if (!selectedFacility) {
    return (
      <div className="min-h-screen bg-white py-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Header with logo and logout */}
          <div className="text-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_fieldmaster-24/artifacts/iuxgdyho_Spaces-Logo.png" 
              alt="Spaces by TRP Sports" 
              className="h-14 mx-auto mb-2"
            />
            <h1 className="text-xl font-bold text-gray-900">Spaces by TRP Sports</h1>
            <p className="text-sm text-gray-500">Booking Platform</p>
          </div>
          
          <div className="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-600">Welcome,</p>
              <p className="font-semibold">{customer?.name || customer?.phone}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>

          <Card className="border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-700">Select Facility</CardTitle>
              <CardDescription>Choose a sports facility to book</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {facilities.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No facilities available</p>
              ) : (
                facilities.map((fac) => (
                  <div
                    key={fac.id}
                    onClick={() => handleFacilitySelect(fac)}
                    className="p-4 border-2 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    <div className="flex items-center">
                      {fac.logo_url ? (
                        <div className="h-12 w-12 mr-3 flex items-center justify-center bg-gray-900 rounded-lg overflow-hidden">
                          <img 
                            src={fac.logo_url} 
                            alt={fac.name} 
                            className="h-10 w-full object-contain"
                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                          />
                        </div>
                      ) : (
                        <Building2 className="h-8 w-8 text-blue-600 mr-3" />
                      )}
                      <div>
                        <h3 className="font-semibold">{fac.name}</h3>
                        <div className="flex gap-1 mt-1">
                          {fac.sports?.map(sport => (
                            <span key={sport} className="text-xs bg-gray-100 px-2 py-0.5 rounded capitalize">
                              {sport}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Booking History View
  if (view === 'history') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <Button variant="ghost" onClick={() => setView('booking')}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="text-xl font-bold">My Bookings</h1>
            <div></div>
          </div>

          <div className="space-y-4">
            {bookingHistory.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  No bookings yet
                </CardContent>
              </Card>
            ) : (
              bookingHistory.map((booking) => (
                <Card key={booking.id}>
                  <CardContent className="py-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{formatDate(booking.date)}</p>
                        <p className="text-sm text-gray-600">
                          {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                        </p>
                        <p className="text-sm text-gray-500 capitalize">{booking.slot_type} Ground</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">₹{booking.amount}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // Pending Payment Screen
  if (pendingBooking && !bookingConfirmed) {
    const totalPrice = calculateTotalPrice();
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white py-8 px-4">
        <div className="max-w-md mx-auto">
          <Card className="border-yellow-200">
            <CardHeader className="text-center">
              <CreditCard className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
              <CardTitle className="text-yellow-700">Complete Payment</CardTitle>
              <CardDescription>Your booking is saved. Complete payment to confirm.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg space-y-2">
                <p><strong>Booking ID:</strong> {pendingBooking.id.slice(0, 8)}...</p>
                <p><strong>Date:</strong> {formatDate(pendingBooking.date)}</p>
                <p><strong>Time:</strong> {formatTime(pendingBooking.start_time)} - {formatTime(pendingBooking.end_time)}</p>
                <p className="text-xl font-bold text-blue-600">₹{pendingBooking.amount}</p>
              </div>
              <Button onClick={() => handlePayment(pendingBooking, totalPrice)} className="w-full" disabled={processingPayment}>
                {processingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                {processingPayment ? 'Processing...' : 'Pay Now'}
              </Button>
              <Button onClick={resetBooking} variant="outline" className="w-full">Cancel</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============ COMING SOON - Temporarily disable slot booking ============
  if (view === 'booking') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col items-center justify-center p-6" data-testid="booking-coming-soon">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#0A2C93]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calendar className="h-10 w-10 text-[#0A2C93]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Online Booking Coming Soon!</h1>
          <p className="text-gray-500 mb-8">
            We're working on bringing online slot booking to you. In the meantime, you can join community games or contact the venue directly to book.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/explore')} className="w-full bg-[#0A2C93] hover:bg-[#082273]">
              Explore Venues
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }
  // ============ END COMING SOON ============

  // Booking Confirmed Screen
  if (bookingConfirmed && confirmedBooking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
        <div className="max-w-md mx-auto">
          <Card className="border-green-200">
            <CardHeader className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <CardTitle className="text-green-700">Booking Confirmed!</CardTitle>
              <CardDescription>Booking ID: {confirmedBooking.id.slice(0, 8)}...</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg space-y-2">
                <p><strong>Facility:</strong> {selectedFacility?.name}</p>
                <p><strong>Date:</strong> {formatDate(confirmedBooking.date)}</p>
                <p><strong>Time:</strong> {formatTime(confirmedBooking.start_time)} - {formatTime(confirmedBooking.end_time)}</p>
                <p><strong>Ground:</strong> {confirmedBooking.slot_type?.toUpperCase()}</p>
                <p><strong>Amount:</strong> ₹{confirmedBooking.amount}</p>
                <p className="text-green-600 font-semibold">✓ Payment Complete</p>
              </div>
              <Button onClick={resetBooking} className="w-full">Make Another Booking</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Booking Flow
  return (
    <div className="min-h-screen bg-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header with logos */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_fieldmaster-24/artifacts/iuxgdyho_Spaces-Logo.png" 
              alt="Spaces by TRP Sports" 
              className="h-10"
            />
            {selectedFacility?.logo_url && (
              <div className="h-10 px-2 flex items-center justify-center bg-gray-900 rounded-lg overflow-hidden">
                <img 
                  src={selectedFacility.logo_url} 
                  alt={selectedFacility.name} 
                  className="h-8 object-contain"
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setView('history'); loadBookingHistory(); }}>
              <History className="h-4 w-4 mr-1" /> My Bookings
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Facility Info */}
        <div className="flex items-center mb-6">
          <Button variant="ghost" size="sm" onClick={() => setSelectedFacility(null)} className="mr-2">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-lg">{selectedFacility?.name}</h1>
            <p className="text-sm text-gray-500">Book your slot</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { resetBookingState(); setSelectedFacility(null); }}
            className="text-blue-600"
          >
            Change
          </Button>
        </div>

        {/* Step Progress */}
        <div className="flex items-center justify-center mb-6">
          {(() => {
            const isFootball = selectedFacility?.sports?.includes('football');
            const steps = isFootball ? [1, 2, 3, 4] : [1, 2, 3, 3.5, 4];
            const stepLabels = isFootball 
              ? ['Date', 'Ground', 'Time', 'Confirm']
              : ['Date', 'Sport', 'Time', 'Court', 'Confirm'];
            
            return steps.map((s, idx) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`} title={stepLabels[idx]}>
                  {idx + 1}
                </div>
                {idx < steps.length - 1 && <div className={`w-8 sm:w-12 h-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ));
          })()}
        </div>

        {/* Step 1: Select Date */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><Calendar className="h-5 w-5 mr-2" /> Select Date</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {getDateOptions().map((date) => (
                  <Button
                    key={date}
                    variant={selectedDate === date ? "default" : "outline"}
                    onClick={() => { setSelectedDate(date); setStep(2); }}
                    className="h-auto py-3"
                  >
                    <div className="text-center">
                      <div className="text-xs">{new Date(date).toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                      <div className="font-bold">{new Date(date).getDate()}</div>
                      <div className="text-xs">{new Date(date).toLocaleDateString('en-IN', { month: 'short' })}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Ground/Court Type */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><Users className="h-5 w-5 mr-2" /> Select {selectedFacility?.sports?.includes('football') ? 'Ground' : 'Sport'}</CardTitle>
              <CardDescription>Selected: {formatDate(selectedDate)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Render based on facility type */}
              {(() => {
                const isFootball = selectedFacility?.sports?.includes('football');
                
                if (isFootball) {
                  // Football: Show slot types (full, half, quarter, 5aside)
                  const typeNames = {
                    'full': { name: 'Full Ground (11 vs 11)', desc: 'Complete football field' },
                    'half': { name: 'Half Ground (9 vs 9)', desc: 'North or South half' },
                    'quarter': { name: 'Quarter Ground (7 vs 7)', desc: 'Any quarter section' },
                    '5aside': { name: '5-Aside', desc: 'Independent 5v5 field' }
                  };
                  
                  // Get unique court types
                  const uniqueTypes = [...new Set(selectedFacility.courts?.map(c => c.court_type) || [])];
                  
                  return uniqueTypes.map(courtType => {
                    const typeInfo = typeNames[courtType] || { name: courtType, desc: '' };
                    return (
                      <Button
                        key={courtType}
                        variant={selectedSlotType === courtType ? "default" : "outline"}
                        onClick={() => {
                          setSelectedSlotType(courtType);
                          if (courtType === 'full' || courtType === '5aside') {
                            setSelectedSection(courtType === 'full' ? null : '5aside');
                            setStep(3);
                          } else {
                            setStep(2.5);
                          }
                        }}
                        className="w-full h-auto py-4 justify-start"
                      >
                        <div>
                          <div className="font-bold">{typeInfo.name}</div>
                          <div className="text-sm opacity-80">{typeInfo.desc}</div>
                        </div>
                      </Button>
                    );
                  });
                } else {
                  // PicknPadel: Show sport types (Padel, Pickle)
                  const uniqueSports = [...new Set(selectedFacility?.courts?.map(c => c.sport) || [])];
                  const sportInfo = {
                    'padel': { name: 'Padel', desc: 'Fast-paced racquet sport', icon: '🎾' },
                    'pickle': { name: 'Pickleball', desc: 'Fun paddle sport', icon: '🏓' }
                  };
                  
                  return uniqueSports.map(sport => {
                    const info = sportInfo[sport] || { name: sport, desc: '', icon: '🏟️' };
                    const courtCount = selectedFacility.courts?.filter(c => c.sport === sport).length || 0;
                    
                    return (
                      <Button
                        key={sport}
                        variant={selectedSlotType === sport ? "default" : "outline"}
                        onClick={() => {
                          setSelectedSlotType(sport);
                          setSelectedSection(null); // Will be selected after time slots
                          setStep(3); // Go to time selection
                        }}
                        className="w-full h-auto py-4 justify-start"
                      >
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{info.icon}</span>
                          <div>
                            <div className="font-bold">{info.name}</div>
                            <div className="text-sm opacity-80">{info.desc} • {courtCount} courts available</div>
                          </div>
                        </div>
                      </Button>
                    );
                  });
                }
              })()}
              
              <Button variant="ghost" onClick={() => setStep(1)} className="w-full">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2.5: Select Section (for half/quarter) */}
        {step === 2.5 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Section</CardTitle>
              <CardDescription>{selectedSlotType === 'half' ? '9 vs 9' : '7 vs 7'} Ground</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {getSections().map((section) => (
                <Button
                  key={section.id}
                  variant={selectedSection === section.id ? "default" : "outline"}
                  onClick={() => { setSelectedSection(section.id); setStep(3); }}
                  className="w-full"
                >
                  {section.name}
                </Button>
              ))}
              <Button variant="ghost" onClick={() => { setStep(2); setSelectedSlotType(''); }} className="w-full">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select Time Slots */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><Clock className="h-5 w-5 mr-2" /> Select Time</CardTitle>
              <CardDescription>
                {formatDate(selectedDate)} • {(() => {
                  const isFootball = selectedFacility?.sports?.includes('football');
                  if (isFootball) {
                    return selectedSlotType === 'full' ? 'Full Ground' : selectedSection?.replace('-', ' ');
                  }
                  return selectedSlotType?.charAt(0).toUpperCase() + selectedSlotType?.slice(1);
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSlots ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
                    {timeSlots.map((time) => {
                      const isFootball = selectedFacility?.sports?.includes('football');
                      let status = 'unavailable';
                      
                      // Check if slot has already passed (for today only)
                      const isPast = isSlotPassed(selectedDate, time);
                      
                      if (isPast) {
                        status = 'passed';
                      } else if (isFootball) {
                        // Football: check specific section
                        const section = selectedSlotType === 'full' ? 'full' : selectedSection;
                        status = getSlotStatus(time, section);
                      } else {
                        // PicknPadel: check if ANY court of this sport type is available
                        const courtsOfSport = selectedFacility?.courts?.filter(c => c.sport === selectedSlotType) || [];
                        for (const court of courtsOfSport) {
                          if (getSlotStatus(time, court.id) === 'available') {
                            status = 'available';
                            break;
                          }
                        }
                      }
                      
                      const isSelected = selectedTimeSlots.includes(time);
                      const price = getSlotPriceFor30Min(selectedSlotType, time);
                      const isDisabled = status !== 'available';
                      
                      return (
                        <button
                          key={time}
                          onClick={() => handleTimeSlotClick(time)}
                          disabled={isDisabled}
                          className={`p-2 rounded-lg text-center text-sm transition-all ${
                            isSelected
                              ? 'bg-blue-600 text-white border-2 border-blue-700'
                              : status === 'available'
                                ? 'bg-white border-2 border-gray-200 hover:border-blue-400 cursor-pointer'
                                : status === 'passed'
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-2 border-gray-200 opacity-50'
                                  : 'bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-300 opacity-60'
                          }`}
                        >
                          <div className="font-medium">{formatTime(time)}</div>
                          {status === 'available' && <div className="text-xs">₹{price}</div>}
                          {status === 'passed' && <div className="text-xs">Passed</div>}
                          {status !== 'available' && status !== 'passed' && <div className="text-xs">Booked</div>}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm mb-4">
                    <div className="flex items-center"><div className="w-4 h-4 bg-white border-2 border-gray-200 rounded mr-1"></div> Available</div>
                    <div className="flex items-center"><div className="w-4 h-4 bg-gray-200 rounded mr-1"></div> Passed</div>
                    <div className="flex items-center"><div className="w-4 h-4 bg-gray-300 rounded mr-1"></div> Booked</div>
                    <div className="flex items-center"><div className="w-4 h-4 bg-blue-600 rounded mr-1"></div> Selected</div>
                  </div>

                  {selectedTimeSlots.length > 0 && (
                    <div className="bg-blue-50 p-3 rounded-lg mb-4">
                      <p className="font-semibold">Selected: {selectedTimeSlots.length} slot(s) ({selectedTimeSlots.length * 30} mins)</p>
                      <p className="text-lg font-bold text-blue-600">Total: ₹{calculateTotalPrice()}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => {
                      const isFootball = selectedFacility?.sports?.includes('football');
                      if (isFootball) {
                        setStep(selectedSlotType === 'full' || selectedSlotType === '5aside' ? 2 : 2.5);
                      } else {
                        setStep(2);
                      }
                    }} className="flex-1">
                      <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <Button onClick={() => {
                      const isFootball = selectedFacility?.sports?.includes('football');
                      if (isFootball) {
                        setStep(4); // Go to confirm
                      } else {
                        setStep(3.5); // Go to court selection for PicknPadel
                      }
                    }} disabled={selectedTimeSlots.length === 0} className="flex-1">
                      Continue <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3.5: Select Court (for PicknPadel after time selection) */}
        {step === 3.5 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><Users className="h-5 w-5 mr-2" /> Select Court</CardTitle>
              <CardDescription>
                {formatDate(selectedDate)} • {formatTime(selectedTimeSlots[0])} - {formatTime(timeSlots[timeSlots.indexOf(selectedTimeSlots[selectedTimeSlots.length - 1]) + 1] || '23:30')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-gray-600 mb-3">Choose an available {selectedSlotType} court:</p>
              
              {selectedFacility?.courts?.filter(c => c.sport === selectedSlotType).map(court => {
                // Check if this court is available for ALL selected time slots
                const isAvailable = selectedTimeSlots.every(time => getSlotStatus(time, court.id) === 'available');
                
                return (
                  <Button
                    key={court.id}
                    variant={selectedSection === court.id ? "default" : "outline"}
                    onClick={() => {
                      if (isAvailable) {
                        setSelectedSection(court.id);
                      }
                    }}
                    disabled={!isAvailable}
                    className={`w-full h-auto py-4 justify-between ${!isAvailable ? 'opacity-50' : ''}`}
                  >
                    <div className="text-left">
                      <div className="font-bold">{court.name}</div>
                      <div className="text-sm opacity-80">
                        {isAvailable ? 'Available for selected time' : 'Not available for all selected slots'}
                      </div>
                    </div>
                    {isAvailable && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Available</span>
                    )}
                  </Button>
                );
              })}
              
              <div className="flex gap-2 pt-3">
                <Button variant="ghost" onClick={() => setStep(3)} className="flex-1">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button onClick={() => setStep(4)} disabled={!selectedSection} className="flex-1">
                  Continue <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirm & Pay */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center"><IndianRupee className="h-5 w-5 mr-2" /> Confirm Booking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 mb-4">
                <p><strong>Facility:</strong> {selectedFacility?.name}</p>
                <p><strong>Date:</strong> {formatDate(selectedDate)}</p>
                <p><strong>Time:</strong> {formatTime(selectedTimeSlots[0])} - {formatTime(timeSlots[timeSlots.indexOf(selectedTimeSlots[selectedTimeSlots.length - 1]) + 1] || '23:30')}</p>
                <p><strong>Duration:</strong> {selectedTimeSlots.length * 30} minutes</p>
                <p><strong>{selectedFacility?.sports?.includes('football') ? 'Ground' : 'Court'}:</strong> {(() => {
                  const isFootball = selectedFacility?.sports?.includes('football');
                  if (isFootball) {
                    return selectedSlotType === 'full' ? 'Full Ground (11v11)' : selectedSection?.replace('-', ' ');
                  }
                  // PicknPadel: show court name
                  const court = selectedFacility?.courts?.find(c => c.id === selectedSection);
                  return court?.name || selectedSection;
                })()}</p>
                <p className="text-xl font-bold text-blue-600 pt-2 border-t">Total: ₹{calculateTotalPrice()}</p>
              </div>

              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    required
                    value={customerDetails.name}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email (for confirmation)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerDetails.email}
                    onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => setStep(3)} className="flex-1">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  <Button type="submit" disabled={submitting || !customerDetails.name} className="flex-1">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                    {submitting ? 'Processing...' : `Pay ₹${calculateTotalPrice()}`}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PublicBooking;
